import React, { useMemo, useState } from 'react';
import { get, ref, set } from 'firebase/database';
import { database } from '../firebase';

type MoneyCurrency = 'COP' | 'USD';
type ToastType = 'neutral' | 'success' | 'error';

type ProjectRevenueNode = {
  Nombre?: unknown;
  Email?: unknown;
  type?: unknown;
  type_price?: unknown;
  status?: unknown;
  bold_status?: unknown;
  Payment?: unknown;
  AdminPayment?: unknown;
};

type PaymentDetails = {
  value: number | null;
  currency: string;
  timestamp: number | null;
  boldTransactionId: string;
};

const getText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const getPositiveNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
};

const readPaymentDetails = (value: unknown): PaymentDetails => {
  const node = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    value: getPositiveNumber(node.value),
    currency: getText(node.currency),
    timestamp: getPositiveNumber(node.timestamp),
    boldTransactionId: getText(node.bold_transaction_id),
  };
};

const formatTimestamp = (timestamp: number | null): string => {
  if (!timestamp) {
    return '-';
  }
  const timestampText = String(Math.trunc(timestamp));
  const timestampMs = timestampText.length > 13
    ? Number(timestampText.slice(0, 13))
    : timestamp > 100000000000
      ? timestamp
      : timestamp * 1000;
  const date = new Date(timestampMs);
  if (!Number.isFinite(date.getTime())) {
    return '-';
  }
  return new Intl.DateTimeFormat('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  }).format(date);
};

const formatMoney = (value: number | null, currency: string): string => {
  if (value === null || !currency) {
    return '-';
  }
  if (currency === 'COP') {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);
  }
  if (currency === 'USD') {
    return `USD ${value.toFixed(value !== 0 && Math.abs(value) < 0.1 ? 4 : 2)}`;
  }
  return `${currency} ${value}`;
};

const couponAlphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const generateCouponCode = (): string => {
  const values = new Uint32Array(8);
  window.crypto.getRandomValues(values);
  const characters = Array.from(values, (value) => couponAlphabet[value % couponAlphabet.length]);
  return `${characters.slice(0, 4).join('')}-${characters.slice(4).join('')}`;
};

const getBogotaUnixTimestamp = (dateTimeValue: string): number | null => {
  const match = dateTimeValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute] = match;
  const timestampMs = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour) + 5,
    Number(minute)
  );
  return Number.isFinite(timestampMs) ? Math.trunc(timestampMs / 1000) : null;
};

function ProjectRevenueSection() {
  const [projectId, setProjectId] = useState('');
  const [project, setProject] = useState<ProjectRevenueNode | null>(null);
  const [loadedProjectId, setLoadedProjectId] = useState('');
  const [adminValueInput, setAdminValueInput] = useState('');
  const [adminCurrency, setAdminCurrency] = useState<MoneyCurrency>('COP');
  const [couponExpiresAtInput, setCouponExpiresAtInput] = useState('');
  const [generatedCouponCode, setGeneratedCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState('');
  const [couponStatusType, setCouponStatusType] = useState<ToastType>('neutral');
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState<ToastType>('neutral');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingCoupon, setIsGeneratingCoupon] = useState(false);

  const boldPayment = useMemo(() => readPaymentDetails(project?.Payment), [project]);
  const adminPayment = useMemo(() => readPaymentDetails(project?.AdminPayment), [project]);
  const normalizedProjectId = projectId.trim();
  const normalizedLoadedProjectId = loadedProjectId.trim();
  const parsedAdminValue = Number(adminValueInput);
  const canSave =
    normalizedLoadedProjectId.length > 0 &&
    normalizedProjectId === normalizedLoadedProjectId &&
    Number.isFinite(parsedAdminValue) &&
    parsedAdminValue > 0;

  const showStatus = (message: string, type: ToastType) => {
    setStatus(message);
    setStatusType(type);
  };

  const showCouponStatus = (message: string, type: ToastType) => {
    setCouponStatus(message);
    setCouponStatusType(type);
  };

  const loadProject = async () => {
    if (!normalizedProjectId) {
      setProject(null);
      setLoadedProjectId('');
      showStatus('Ingresa un Project ID válido.', 'error');
      return;
    }

    setIsLoading(true);
    setStatus('');
    try {
      const snapshot = await get(ref(database, `Projects/${normalizedProjectId}`));
      if (!snapshot.exists()) {
        setProject(null);
        setLoadedProjectId('');
        showStatus('El proyecto no existe.', 'error');
        return;
      }

      const value = snapshot.val() as ProjectRevenueNode;
      const nextAdminPayment = readPaymentDetails(value.AdminPayment);
      setProject(value);
      setLoadedProjectId(normalizedProjectId);
      setAdminValueInput(nextAdminPayment.value === null ? '' : String(nextAdminPayment.value));
      setAdminCurrency(nextAdminPayment.currency === 'USD' ? 'USD' : 'COP');
      showStatus('Proyecto cargado.', 'success');
    } catch (error) {
      setProject(null);
      setLoadedProjectId('');
      showStatus('No se pudo cargar el proyecto.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const saveAdminPayment = async () => {
    if (!canSave) {
      showStatus('Ingresa un valor positivo para guardar.', 'error');
      return;
    }

    setIsSaving(true);
    setStatus('');
    try {
      await set(ref(database, `Projects/${normalizedLoadedProjectId}/AdminPayment`), {
        value: parsedAdminValue,
        currency: adminCurrency,
      });
      await loadProject();
      showStatus('Ingreso admin guardado.', 'success');
    } catch (error) {
      showStatus('No se pudo guardar el ingreso admin.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAdminPayment = async () => {
    if (!normalizedLoadedProjectId || normalizedProjectId !== normalizedLoadedProjectId) {
      showStatus('Carga un proyecto antes de eliminar.', 'error');
      return;
    }

    setIsSaving(true);
    setStatus('');
    try {
      await set(ref(database, `Projects/${normalizedLoadedProjectId}/AdminPayment`), null);
      await loadProject();
      showStatus('Ingreso admin eliminado.', 'success');
    } catch (error) {
      showStatus('No se pudo eliminar el ingreso admin.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const generateCoupon = async () => {
    const expiresAt = getBogotaUnixTimestamp(couponExpiresAtInput);
    const currentTimestamp = Math.trunc(Date.now() / 1000);
    if (!expiresAt || expiresAt <= currentTimestamp) {
      showCouponStatus('Ingresa una fecha futura en horario Bogotá.', 'error');
      return;
    }

    setIsGeneratingCoupon(true);
    setCouponStatus('');
    setGeneratedCouponCode('');
    try {
      let code = '';
      for (let attempt = 0; attempt < 20; attempt += 1) {
        const candidate = generateCouponCode();
        const snapshot = await get(ref(database, `Coupons/${candidate}`));
        if (!snapshot.exists()) {
          code = candidate;
          break;
        }
      }

      if (!code) {
        showCouponStatus('No se pudo generar un código único.', 'error');
        return;
      }

      await set(ref(database, `Coupons/${code}`), {
        code,
        expiresAt,
      });
      setGeneratedCouponCode(code);
      showCouponStatus('Cupón generado.', 'success');
    } catch (error) {
      showCouponStatus('No se pudo generar el cupón.', 'error');
    } finally {
      setIsGeneratingCoupon(false);
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Ingresos Proyectos</h2>
      </div>

      <form
        className="project-search"
        onSubmit={(event) => {
          event.preventDefault();
          void loadProject();
        }}
      >
        <label className="field">
          <span>Project ID</span>
          <input
            type="text"
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
              setProject(null);
              setLoadedProjectId('');
            }}
            placeholder="ID del proyecto"
          />
        </label>
        <button className="primary-button" type="submit" disabled={isLoading}>
          {isLoading ? 'Buscando...' : 'Buscar'}
        </button>
        {status ? <div className={`inline-toast inline-toast-${statusType}`}>{status}</div> : null}
      </form>

      <div className="project-tables">
        <div className="project-table">
          <div className="project-table-header">
            <h3>Cupón de descuento</h3>
            {generatedCouponCode ? <div className="coupon-code-pill mono">{generatedCouponCode}</div> : null}
          </div>
          <div className="project-revenue-form coupon-generation-form">
            <label className="field">
              <span>Fecha de expiración:</span>
              <input
                className="table-input"
                type="datetime-local"
                value={couponExpiresAtInput}
                onChange={(event) => setCouponExpiresAtInput(event.target.value)}
              />
            </label>
            <div className="project-revenue-actions">
              <button
                className="primary-button"
                type="button"
                onClick={generateCoupon}
                disabled={isGeneratingCoupon}
              >
                {isGeneratingCoupon ? 'Generando...' : 'Generar cupón de descuento'}
              </button>
            </div>
            {couponStatus ? (
              <div className={`inline-toast inline-toast-${couponStatusType}`}>{couponStatus}</div>
            ) : null}
          </div>
        </div>
      </div>

      {project ? (
        <div className="project-tables">
          <div className="project-table">
            <div className="project-table-header">
              <h3>Proyecto</h3>
            </div>
            <div className="table table-compact">
              <div className="table-row project-revenue-detail-row">
                <div>Nombre</div>
                <div>{getText(project.Nombre) || '-'}</div>
              </div>
              <div className="table-row project-revenue-detail-row">
                <div>Email</div>
                <div className="metrics-admin-email">{getText(project.Email) || '-'}</div>
              </div>
              <div className="table-row project-revenue-detail-row">
                <div>Tipo</div>
                <div>{getText(project.type) || '-'}</div>
              </div>
              <div className="table-row project-revenue-detail-row">
                <div>Tipo precio</div>
                <div>{getText(project.type_price) || '-'}</div>
              </div>
            </div>
          </div>

          <div className="project-table">
            <div className="project-table-header">
              <h3>Payment Bold</h3>
            </div>
            <div className="table table-compact">
              <div className="table-row project-revenue-detail-row">
                <div>Valor</div>
                <div>{formatMoney(boldPayment.value, boldPayment.currency)}</div>
              </div>
              <div className="table-row project-revenue-detail-row">
                <div>Moneda</div>
                <div>{boldPayment.currency || '-'}</div>
              </div>
              <div className="table-row project-revenue-detail-row">
                <div>Fecha</div>
                <div>{formatTimestamp(boldPayment.timestamp)}</div>
              </div>
              <div className="table-row project-revenue-detail-row">
                <div>Transacción</div>
                <div className="mono">{boldPayment.boldTransactionId || '-'}</div>
              </div>
              {getText(project.bold_status) ? (
                <div className="table-row project-revenue-detail-row">
                  <div>Bold status</div>
                  <div>{getText(project.bold_status)}</div>
                </div>
              ) : <div className="table-row project-revenue-detail-row">
                <div>Bold status</div>
                <div>{getText(project.status) || '-'}</div>
              </div>}
            </div>
          </div>

          <div className="project-table">
            <div className="project-table-header">
              <h3>Ingresos Directos</h3>
              <div className="project-total">Actual: {formatMoney(adminPayment.value, adminPayment.currency)}</div>
            </div>
            <div className="project-revenue-form">
              <label className="field">
                <span>Valor</span>
                <input
                  className="table-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={adminValueInput}
                  onChange={(event) => setAdminValueInput(event.target.value)}
                />
              </label>
              <label className="field">
                <span>Moneda</span>
                <select
                  className="table-input"
                  value={adminCurrency}
                  onChange={(event) => setAdminCurrency(event.target.value as MoneyCurrency)}
                >
                  <option value="COP">COP</option>
                  <option value="USD">USD</option>
                </select>
              </label>
              <div className="project-revenue-actions">
                <button className="primary-button" type="button" onClick={saveAdminPayment} disabled={isSaving || !canSave}>
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
                <button className="secondary-button" type="button" onClick={deleteAdminPayment} disabled={isSaving}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProjectRevenueSection;
